/**
 * Client-side batch upsert chunking and error discrimination (EHQ-104).
 *
 * Mirrors ehq-server `MAX_BATCH_PARTS` / default `max_batch_size`. The server
 * does not advertise these at runtime, so the client must stay inside them:
 * over the limit is `400` with no sequential fallback.
 */

import type { BatchUpsertResponse } from '@app/shared/interfaces/file.interface';

/** Mirrors ehq-server `batch_upsert::MAX_BATCH_PARTS`. */
export const MAX_BATCH_PARTS = 100;

/** Mirrors ehq-server default `ApplicationSettings::max_batch_size` (64 MiB). */
export const MAX_BATCH_BYTES = 64 * 1024 * 1024;

export interface ChunkableUpload {
  fileName: string;
  blob: { size: number };
}

/**
 * Split pending uploads into batches that fit the server's part-count and
 * aggregate-byte caps. A single file larger than `maxBytes` is isolated so it
 * cannot poison a batch of otherwise-legal files.
 */
export function chunkSyncUploads<T extends ChunkableUpload>(
  uploads: T[],
  maxParts: number = MAX_BATCH_PARTS,
  maxBytes: number = MAX_BATCH_BYTES
): T[][] {
  const chunks: T[][] = [];
  let current: T[] = [];
  let currentBytes = 0;

  for (const upload of uploads) {
    const size = upload.blob.size;
    const exceedsOwnBudget = size > maxBytes;
    const wouldOverflow =
      current.length > 0 &&
      (current.length >= maxParts || currentBytes + size > maxBytes);

    if (exceedsOwnBudget || wouldOverflow) {
      if (current.length > 0) {
        chunks.push(current);
        current = [];
        currentBytes = 0;
      }
    }

    current.push(upload);
    currentBytes += size;

    if (exceedsOwnBudget) {
      chunks.push(current);
      current = [];
      currentBytes = 0;
    }
  }

  if (current.length > 0) chunks.push(current);
  return chunks;
}

/**
 * True only when a 404 means the batch *route* is missing (old server).
 *
 * Round-3 ehq-server also returns 404 for a foreign cache_id (bare NotFound)
 * and for all-item NOT_FOUND (body still carries `results`). Those are real
 * batch responses — falling back to sequential would re-issue doomed requests.
 */
export function isBatchEndpointAbsent(batchError: unknown): boolean {
  if (!isRecord(batchError) || batchError['status'] !== 404) return false;
  return !batchResponseBody(batchError);
}

/**
 * Pull a batch response body off an Angular HttpErrorResponse (or a test
 * double). All-item failure keeps `results` on 400/403/404.
 */
export function batchResponseBody(batchError: unknown): BatchUpsertResponse | null {
  if (!isRecord(batchError)) return null;
  const candidates = [batchError['error'], batchError];
  for (const candidate of candidates) {
    if (isRecord(candidate) && Array.isArray(candidate['results'])) {
      return candidate as unknown as BatchUpsertResponse;
    }
  }
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object';
}
